<template>
  <section class="maix-conversation" :class="{ 'console-layout': consoleLayout }">
    <header v-if="!consoleLayout"><div><h3>Maix Message</h3><p>App, email, and text messages with this client.</p></div>
    </header>
    <div class="content">
      <div class="conversation-header">
      <div class="feed-toolbar">
        <MessageToolsDrawer ref="toolsDrawer">
      <div class="app-destinations">
        <strong>Publish to app request threads:</strong>
        <label v-for="request in requests" :key="request.id" class="request-destination">
          <input v-model="selectedRequestIds" type="checkbox" :value="request.id" :disabled="loading || sending || !!pending">
          <span>{{ request.title }}</span>
        </label>
        <p v-if="!requests.length" class="hint">No attorney request threads available.</p>
        <p class="hint">Select any requests to receive this message in the app. Leave all unchecked to send only email or text.</p>
      </div>
        <div class="channels">
          <strong>Also publish to:</strong>
          <label v-if="capabilities.email?.available"><input v-model="emailSelected" type="checkbox" :disabled="loading || sending || !!pending"> Email</label>
          <label v-if="capabilities.sms?.available"><input v-model="smsSelected" type="checkbox" :disabled="loading || sending || !!pending"> Text</label>
          <button v-if="requests.length || capabilities.email?.available || capabilities.sms?.available" type="button" :disabled="loading || sending || !!pending" @click="selectAll">Choose all</button>
        </div>
        <p v-if="selectedRequestIds.length > 1" class="hint">Email and text send once each. Replies appear in All messages.</p>
        <p v-if="selectedRequestIds.length && (!capabilities.email?.available || !capabilities.sms?.available)" class="hint">Email and text are shown only when available and permitted for every selected request.</p>
      <label v-if="!requestId && requests.length" class="request-picker">View thread:
        <select v-model="selectedRequest"><option value="">All messages</option>
          <option v-for="request in requests" :key="request.id" :value="request.id">{{ request.title }}</option>
        </select>
      </label>
        <label v-if="emailSelected">Email subject<input v-model="subject" maxlength="200" :disabled="sending || !!pending" required></label>
          <button v-if="!consoleLayout" type="button" :disabled="loading || sending" @click="refresh">Refresh messages</button>
          <label class="metadata-toggle"><input v-model="showMetadata" type="checkbox"> Show metadata</label>
          <slot name="tools" />
        </MessageToolsDrawer>
        <strong v-if="!consoleLayout">Client conversation</strong>
        <div class="filter-slot" :aria-busy="loading">
        <div class="scope-toggle" :class="{ 'is-loading': loading }" role="group" aria-label="Message filter">
          <button type="button" :aria-pressed="scope === 'all'" @click="scope = 'all'">All messages</button>
          <button type="button" :aria-pressed="scope === 'request'" :disabled="!activeRequest" @click="scope = 'request'">This request</button>
        </div><span v-if="loading" class="loading-status" role="status">Loading…</span>
        </div>
        <button v-if="cursor" type="button" :disabled="loading" @click="loadOlder">Load older messages</button>
      </div>
      <p v-if="scope === 'request'" class="hint">Showing messages linked to this request. Text replies without a request link appear in All messages.</p>
      </div>
      <div ref="feed" class="feed" aria-live="polite">
        <p v-if="!loading && !messages.length" class="hint">{{ scope === 'request' ? 'No messages linked to this request yet.' : 'No client-facing messages yet.' }}</p>
        <article v-for="message in messages" :key="message.id" :class="['message', message.direction]">
          <div class="bubble">
            <button v-if="showRequestLinks && message.requestId && !(message.requestLinks?.length > 1)" class="open-request" type="button" title="Open attorney request" aria-label="Open attorney request" @click="$emit('open-request', message.requestId)">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M10 4H4v16h6M14 7l5 5-5 5M8 12h11" /></svg>
            </button>
            <div v-if="message.requestLinks?.length > 1" class="request-links">
              <button v-for="request in message.requestLinks" :key="request.id" type="button" :disabled="!showRequestLinks" @click="$emit('open-request', request.id)">{{ request.title }}{{ showRequestLinks ? ' ↗' : '' }}</button>
            </div>
            <small v-if="message.requestTitle" class="request-label">{{ message.requestTitle }}{{ message.requestId === activeRequest ? ' · Current request' : '' }}</small>
            <div v-if="message.senderEmail !== null && message.senderEmail !== undefined" :class="['email-sender', { 'sender-mismatch': message.senderMismatch }]">
              <span>From: {{ message.senderEmail || 'Unrecognized sender address' }}</span>
              <strong v-if="message.senderMismatch">Different from the client’s email on file</strong>
            </div>
            <strong v-if="message.subject">{{ message.subject }}</strong>
            <EmailMessageBody v-if="message.direction === 'inbound' && message.statuses.email" :text="message.text" />
            <p v-else>{{ message.text }}</p>
            <div class="metadata"><time>{{ formatDate(message.occurredAt) }}</time>
              <span v-for="(status, channel) in message.statuses" :key="channel">via {{ channel === 'sms' ? 'Text' : channel === 'email' ? 'Email' : 'App' }} · {{ statusLabel(status) }}</span>
            </div>
            <template v-if="showMetadata">
            <small v-if="message.sendId" class="message-debug-id">Send ID: {{ message.sendId }}</small>
            <small v-for="(path, channel) in (message.messageRecords || { message: message.id })" :key="channel" class="message-debug-id">Firestore {{ channel }}: {{ path }}</small>
            </template>
            <button v-if="message.retryId" type="button" :disabled="sending" @click="retry(message.retryId)">Retry unfinished delivery</button>
            <p v-for="(detail, channel) in message.deliveryErrors" :key="channel" class="error">{{ channel }}: {{ detail }}</p>
            <small v-if="message.statuses.sms === 'unconfirmed' || message.statuses.sms === 'sending'" class="hint">Check Twilio before resending this text; acceptance is not confirmed.</small>
          </div>
        </article>
      </div>
      <div class="composer-dock">
      <form @submit.prevent="submit">
        <label><span class="composer-heading"><span>New Message</span><small>{{ text.length }}/{{ smsSelected ? 1600 : 10000 }}</small></span><textarea v-model="text" rows="2" :maxlength="smsSelected ? 1600 : 10000" :disabled="sending || !!pending" placeholder="Write a message to the app client..." required /></label>
        <div class="send-row">
        <div class="message-capsules">
          <button type="button" class="capsule" @click="toolsDrawer.open()">{{ contextLabel }}</button>
          <button type="button" class="capsule" @click="toolsDrawer.open()">Publish to: {{ channelLabel }}</button>
          <button v-if="emailSelected" type="button" class="capsule" @click="toolsDrawer.open()">Subject: {{ subject || 'Required' }}</button>
        </div>
          <button type="submit" :disabled="!canSend">{{ sending ? 'Sending…' : pending ? 'Check / Retry Saved Send' : 'Send Message' }}</button>
        </div>
      </form>

      <p v-if="error" role="alert" class="error">{{ error }}</p>
      <p v-if="notice" role="status">{{ notice }}</p>
      </div>
    </div>
  </section>
</template>

<script>
import MessageToolsDrawer from "./MessageToolsDrawer.vue";
import EmailMessageBody from "./EmailMessageBody.vue";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { fetchMaixClientMessages, sendMaixRequestMessage, retryMaixRequestMessage } from "@/firebase/firestore";

export default {
  name: "MaixClientMessages",
  components: { EmailMessageBody, MessageToolsDrawer },
  props: { consoleLayout: { type: Boolean, default: false }, showRequestLinks: { type: Boolean, default: false }, requestId: { type: String, default: "" }, clientId: { type: String, default: "" }, defaultSubject: { type: String, default: "Attorney Assistance Request" } },
  emits: ["message-sent", "open-request"],
  setup(props, { emit }) {
    const selectedRequest = ref("");
    const selectedRequestIds = ref(props.requestId ? [props.requestId] : []);
    const resolvedClientId = ref(props.clientId);
    const scope = ref("all");
    const activeRequest = computed(() => props.requestId || selectedRequest.value);
    const requests = ref([]);
    const toolsDrawer = ref(null);
    const showMetadata = ref(false);
    const contextLabel = computed(() => selectedRequestIds.value.length > 1 ? `${selectedRequestIds.value.length} app request threads` : selectedRequestIds.value.length ? requests.value.find(request => request.id === selectedRequestIds.value[0])?.title || props.defaultSubject : "General message");
    const messages = ref([]);
    const clientCapabilities = ref({});
    const capabilitiesFor = ids => {
      const selected = ids.map(id => requests.value.find(request => request.id === id));
      return Object.fromEntries(["email", "sms"].map(channel => [channel, { ...clientCapabilities.value[channel],
        available: !!clientCapabilities.value[channel]?.available && selected.every(request => request?.contactChoices?.[channel] === true) }]));
    };
    const capabilities = computed(() => capabilitiesFor(selectedRequestIds.value));
    const cursor = ref(null);
    const text = ref("");
    const subject = ref((props.requestId ? props.defaultSubject : "Message from Maix").slice(0, 200));
    const appSelected = computed(() => selectedRequestIds.value.length > 0);
    const emailSelected = ref(false);
    const smsSelected = ref(false);
    const channelLabel = computed(() => [appSelected.value ? `✓ App (${selectedRequestIds.value.length})` : "", emailSelected.value ? "Email" : "", smsSelected.value ? "Text" : ""].filter(Boolean).join(" · ") || "Choose delivery");
    const pending = ref(null);
    const loading = ref(false);
    const sending = ref(false);
    const error = ref("");
    const notice = ref("");
    const feed = ref(null);
    let disposed = false;
    let loadVersion = 0;
    let hasOlder = false;
    const canSend = computed(() => !loading.value && !sending.value && (pending.value || ((appSelected.value || emailSelected.value || smsSelected.value) && text.value.trim() &&
      text.value.length <= (smsSelected.value ? 1600 : 10000) && (!emailSelected.value || subject.value.trim()) &&
      (!emailSelected.value || capabilities.value.email?.available) && (!smsSelected.value || capabilities.value.sms?.available))));
    const merge = loaded => {
      const map = new Map(messages.value.map(message => [message.id, message]));
      loaded.forEach(message => map.set(message.id, message));
      messages.value = [...map.values()].sort((a, b) => a.occurredAt - b.occurredAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    };
    const load = async older => {
      if (disposed || (older && !cursor.value)) return;
      const version = ++loadVersion;
      loading.value = true;
      const nearBottom = !feed.value || feed.value.scrollHeight - feed.value.scrollTop - feed.value.clientHeight < 80;
      const oldHeight = feed.value?.scrollHeight || 0;
      const oldTop = feed.value?.scrollTop || 0;
      try {
        const result = await fetchMaixClientMessages({ ...(activeRequest.value ? { requestId: activeRequest.value } : { clientId: props.clientId }), scope: scope.value, cursor: older ? cursor.value : null });
        if (disposed || version !== loadVersion) return;
        requests.value = result.requests;
        clientCapabilities.value = result.clientCapabilities || result.capabilities;
        resolvedClientId.value = result.clientId;
        if (older) { hasOlder = true; cursor.value = result.cursor; }
        else if (!hasOlder) { cursor.value = result.cursor; messages.value = []; }
        merge(result.messages);
        await nextTick();
        if (feed.value) {
          if (older) feed.value.scrollTop = oldTop + feed.value.scrollHeight - oldHeight;
          else if (nearBottom) feed.value.scrollTop = feed.value.scrollHeight;
        }
      } catch (failure) {
        if (!disposed && version === loadVersion) error.value = failure?.message || "Unable to load the conversation.";
      } finally {
        if (!disposed && version === loadVersion) loading.value = false;
      }
    };
    const loadLatest = () => load(false);
    const refresh = () => { hasOlder = false; cursor.value = null; messages.value = []; return loadLatest(); };
    const loadOlder = () => load(true);
    const deliver = async action => {
      if (sending.value) return;
      sending.value = true; error.value = ""; notice.value = "";
      try {
        const result = await action();
        if (disposed) return;
        const incomplete = Object.entries(result.statuses).some(([channel, status]) => channel !== "app" && ["pending", "blocked", "sending", "unconfirmed"].includes(status));
        notice.value = incomplete ? "Message saved. A selected delivery needs attention in the conversation." : "Message saved. Selected deliveries have been submitted.";
        if (pending.value?.messageId === result.id) { pending.value = null; text.value = ""; }
        emit("message-sent", { id: result.id, requestId: result.requestId, requestIds: result.requestIds });
      } catch (failure) {
        if (!disposed) error.value = failure?.message || "Unable to confirm this send. Use the same saved send to retry.";
      } finally {
        if (!disposed) { sending.value = false; await loadLatest(); }
      }
    };
    const submit = async () => {
      if (!canSend.value) return;
      if (!pending.value) pending.value = { requestIds: [...selectedRequestIds.value], clientId: resolvedClientId.value, includeApp: appSelected.value, messageId: crypto.randomUUID(),
        text: text.value.trim(), subject: subject.value.trim(), channels: [emailSelected.value ? "email" : null, smsSelected.value ? "sms" : null].filter(Boolean) };
      await deliver(() => sendMaixRequestMessage(pending.value));
    };
    const retry = messageId => deliver(() => retryMaixRequestMessage(messageId));
    const selectAll = () => {
      selectedRequestIds.value = requests.value.map(request => request.id);
      const available = capabilitiesFor(selectedRequestIds.value);
      emailSelected.value = !!available.email?.available; smsSelected.value = !!available.sms?.available;
    };
    const statusLabel = status => ({ sent: "Sent", accepted: "Accepted", delivered: "Delivered", received: "Received", unconfirmed: "Unconfirmed", blocked: "Needs attention", pending: "Pending", sending: "Sending", bounced: "Bounced", complained: "Reported as spam" }[status] || status);
    const formatDate = value => new Date(value).toLocaleString();
    watch(activeRequest, () => {
      error.value = "";
      const desired = activeRequest.value ? "request" : "all";
      if (scope.value !== desired) scope.value = desired;
      else void refresh();
    });
    watch(selectedRequestIds, (ids, previous) => {
      const oldDefault = previous.length === 1 ? requests.value.find(request => request.id === previous[0])?.title || props.defaultSubject : "Message from Maix";
      if (subject.value === oldDefault.slice(0, 200)) subject.value = (ids.length === 1 ? requests.value.find(request => request.id === ids[0])?.title || props.defaultSubject : "Message from Maix").slice(0, 200);
    });
    watch(capabilities, available => {
      if (!available.email?.available) emailSelected.value = false;
      if (!available.sms?.available) smsSelected.value = false;
    });
    watch(scope, () => { error.value = ""; void refresh(); });
    onMounted(loadLatest);
    onBeforeUnmount(() => { disposed = true; loadVersion++; });
    return { toolsDrawer, showMetadata, contextLabel, channelLabel, selectedRequest, selectedRequestIds, scope, activeRequest, requests, messages, capabilities, cursor, text, subject, appSelected, emailSelected, smsSelected,
      pending, loading, sending, error, notice, feed, canSend, refresh, loadLatest, loadOlder, submit, retry, selectAll, statusLabel, formatDate };
  }
};
</script>

<style scoped>
.maix-conversation { position: relative; border: 1px solid #dce5e1; border-radius: 14px; background: white; overflow: hidden; }
header { display: flex; align-items: center; justify-content: space-between; padding: 22px 26px; gap: 12px; border-bottom: 1px solid #e2e9e5; }
h3 { margin: 0; color: #243c32; } header p { margin: 7px 0 0; color: #77877f; }
.content { padding: 24px; } label { display: block; font-weight: 600; color: #40544a; }
textarea, input:not([type=checkbox]), select { display: block; box-sizing: border-box; width: 100%; margin: 8px 0 14px; padding: 12px; border: 1px solid #d1dfd7; border-radius: 9px; font: inherit; }
textarea { resize: vertical; } .channels, .send-row, .feed-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 16px; margin: 16px 0; }
.channels > strong { flex: 0 0 100%; }
.channels label { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 8px; margin: 0; white-space: nowrap; }
.channels input[type=checkbox] { position: static; flex: 0 0 16px; width: 16px; height: 16px; margin: 0; padding: 0; accent-color: #16794f; } .app-channel { color: #16835a; font-weight: 600; }
.composer-heading { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.composer-heading small { font-weight: 400; white-space: nowrap; }
.send-row { justify-content: space-between; flex-wrap: nowrap; }
.send-row > button { flex: 0 0 auto; margin-left: auto; } button { border: 0; border-radius: 8px; background: #16794f; color: white; padding: 10px 16px; cursor: pointer; }
button:disabled { opacity: 0.5; cursor: default; } .hint, small { color: #75867d; font-size: .8rem; } .error { color: #a52838; }
.feed-toolbar { justify-content: space-between; }
.filter-slot { position: relative; }
.scope-toggle.is-loading { visibility: hidden; }
.loading-status { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
.scope-toggle { display: flex; gap: 6px; }
.scope-toggle button[aria-pressed="false"] { background: #eff3f1; color: #40544a; }
.feed { max-height: 520px; overflow-y: auto; padding: 10px; background: #fafcfb; border-radius: 10px; }
.message { display: flex; margin: 12px 0; } .message.outbound { justify-content: flex-end; }
.bubble { width: fit-content; max-width: 88%; padding: 14px 18px; background: #eff3f1; border-radius: 13px; overflow-wrap: anywhere; }
.outbound .bubble { background: #e7f4ed; } .bubble p { white-space: pre-wrap; margin: 8px 0; }
.bubble strong, .request-label, .bubble .hint { display: block; } .metadata { display: flex; flex-wrap: wrap; gap: 5px 12px; font-size: .74rem; color: #6c7d74; }
.bubble .open-request { float: right; margin: 0 0 8px 12px; padding: 6px; display: inline-flex; background: transparent; color: #16794f; }
.bubble button { margin-top: 10px; font-size: .8rem; }

.composer-dock { border-top: 1px solid #e3e9e6; padding-top: 14px; margin-top: 12px; }
.console-layout { flex: 1; min-height: 0; display: flex; border: 0; border-radius: 0; background: transparent; }
.console-layout .content { flex: 1; min-height: 0; min-width: 0; display: flex; flex-direction: column; gap: 12px; padding: 0; }
.console-layout .conversation-header, .console-layout .feed, .console-layout .composer-dock { border: 1px solid #dce5e1; border-radius: 16px; background: white; box-shadow: 0 3px 12px rgba(36, 60, 50, .04); }
.console-layout .conversation-header { flex: 0 0 auto; }
.console-layout .conversation-header > .hint { margin: 0; padding: 0 14px 12px; }
.console-layout .feed-toolbar { flex: 0 0 auto; margin: 0; padding: 10px 14px; }
.console-layout .feed { flex: 1 1 0; min-height: 80px; max-height: none; padding: 14px; }
.console-layout .composer-dock { flex: 0 0 auto; max-height: 55%; overflow-y: auto; margin: 0; padding: 12px 16px; background: white; }
.console-layout .composer-dock select, .console-layout .composer-dock input:not([type=checkbox]), .console-layout textarea { margin: 5px 0 8px; padding: 8px 10px; }
.console-layout textarea { resize: none; min-height: 60px; }
.console-layout .channels { margin: 0; gap: 14px; }
.console-layout .send-row { margin: 8px 0; gap: 12px; }
.console-layout .composer-dock p { margin: 6px 0; }
.app-destinations { display: grid; gap: 14px; }
.request-destination { display: flex; align-items: flex-start; gap: 10px; }
.request-destination input[type=checkbox] { position: static; flex: 0 0 18px; width: 18px; height: 18px; margin: 3px 0 0; accent-color: #16794f; }
.request-links { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.request-links button { margin: 0; padding: 4px 8px; background: #d5eadf; color: #315d45; text-align: left; }
.request-links button:disabled { opacity: 1; }
.metadata-toggle { display: flex; align-items: center; gap: 8px; }
.metadata-toggle input[type=checkbox] { position: static; width: 16px; height: 16px; margin: 0; accent-color: #16794f; }
.message-debug-id { display: block; margin-top: 6px; font-family: monospace; font-size: .7rem; color: #60746b; overflow-wrap: anywhere; user-select: text; }
.email-sender { margin: 6px 0; font-size: .85rem; }
.sender-mismatch { padding: 8px 10px; border: 1px solid #d5a243; border-radius: 7px; background: #fff3cf; color: #684800; }
.message-capsules { min-width: 0; flex: 1; display: flex; flex-wrap: wrap; gap: 6px; }
.message-capsules .capsule { padding: 4px 10px; border-radius: 20px; background: #edf4ef; color: #315d45; font-size: .75rem; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
